import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/lib/logger";

/**
 * Extract a non-PII status signal from a Supabase FunctionsError (transport).
 * Only the numeric status / name are surfaced — never the response body.
 */
function transportSignal(error: unknown): { name: string | null; status: number | null } {
  const e = (error ?? {}) as { name?: unknown; context?: { status?: unknown } };
  const name = typeof e.name === "string" ? e.name : null;
  const status =
    e.context && typeof e.context === "object" && typeof e.context.status === "number"
      ? e.context.status
      : null;
  return { name, status };
}

export type NotificationType =
  | "assignment"
  | "assignment_to_created"
  | "reassignment"
  | "due_reminder"
  | "overdue"
  | "defect_created"
  | "defect_status_changed"
  | "defect_comment"
  | "defect_closed"
  | "inspection_completed"
  // TES-2028: Server schreibt seit Mig 359 type='escalation' (TES-1871).
  | "escalation"
  // TES-2053: DB-Trigger (Mig 380/381) schreibt type='inspection_to_verify' (zu-verifizieren-Task).
  | "inspection_to_verify"
  // TES-2082: DB-Trigger (Mig 388/389) schreibt type='owner_restaffing' bei Owner-Deaktivierung
  // (Re-Staffing an qm_admin + Interim-Hinweis an Deputy; payload.role diskriminiert die Perspektive).
  | "owner_restaffing";

// Structured payload for localization
export interface NotificationPayload {
  entity_title?: string;
  severity?: string;
  status?: string;
  due_date?: string;
  days_overdue?: number;
  position_name?: string;
  is_first_reminder?: boolean;
  assignee_name?: string;
  commenter_name?: string;
  closed_by_name?: string;
  // TES-2082 (Typ 10) / TES-2080 (Typ 6): Anlass-Diskriminator für owner_restaffing-Notifications
  // ("owner_restaffing"/"deputy_interim" = Owner-Vakanz vs. "items_reassignment" = N Items neu zuweisen).
  role?: string;
}

interface SendNotificationParams {
  userId: string;
  type: NotificationType;
  entityType?: "inspection" | "defect" | "task";
  entityId?: string;
  payload: NotificationPayload;
}

export async function sendNotification({
  userId,
  type,
  entityType,
  entityId,
  payload,
}: SendNotificationParams): Promise<boolean> {
  try {
    const { data, error } = await supabase.functions.invoke("send-notification-email", {
      body: {
        user_id: userId,
        notification_type: type,
        entity_type: entityType,
        entity_id: entityId,
        payload,
      },
    });

    if (error) {
      // TRANSPORT channel: the edge function call itself failed (network/HTTP).
      logger.error("Notification transport error", {
        type,
        entityType,
        ...transportSignal(error),
      });
      return false;
    }

    // Dual-Error-Check: APPLICATION channel — edge function returned an error body.
    if (
      !data ||
      (typeof data === "object" &&
        data !== null &&
        "error" in data &&
        (data as Record<string, unknown>).error)
    ) {
      logger.error("Notification application error", {
        type,
        entityType,
        // boolean only — never the error body (may contain PII)
        hasErrorBody: !!(data as Record<string, unknown> | null)?.error,
      });
      return false;
    }

    logger.info("Notification sent", { type, entityType });
    return true;
  } catch (error) {
    // TRANSPORT channel: thrown before/while invoking the edge function.
    logger.error("Notification transport exception", {
      type,
      entityType,
      ...transportSignal(error),
    });
    return false;
  }
}

// Helper functions for common notification scenarios
export async function notifyAssignment(
  userId: string,
  entityType: "inspection" | "defect" | "task",
  entityTitle: string,
  entityId: string,
): Promise<boolean> {
  return sendNotification({
    userId,
    type: "assignment",
    entityType,
    entityId,
    payload: {
      entity_title: entityTitle,
    },
  });
}

export async function notifyReassignment(
  previousAssigneeId: string,
  entityType: "inspection" | "defect",
  entityTitle: string,
  entityId: string,
  newAssigneeName?: string,
): Promise<boolean> {
  return sendNotification({
    userId: previousAssigneeId,
    type: "reassignment",
    entityType,
    entityId,
    payload: {
      entity_title: entityTitle,
      assignee_name: newAssigneeName,
    },
  });
}

export async function notifyCreatorOfAssignment(
  creatorId: string,
  entityType: "inspection" | "defect",
  entityTitle: string,
  entityId: string,
  assigneeName?: string,
): Promise<boolean> {
  return sendNotification({
    userId: creatorId,
    type: "assignment_to_created",
    entityType,
    entityId,
    payload: {
      entity_title: entityTitle,
      assignee_name: assigneeName,
    },
  });
}

export async function notifyDefectCreated(
  userId: string,
  defectTitle: string,
  defectId: string,
  severity: string,
): Promise<boolean> {
  return sendNotification({
    userId,
    type: "defect_created",
    entityType: "defect",
    entityId: defectId,
    payload: {
      entity_title: defectTitle,
      severity,
    },
  });
}

export async function notifyDefectStatusChanged(
  userId: string,
  defectTitle: string,
  defectId: string,
  newStatus: string,
  // Deprecated/no-op options retained for caller compatibility (Teams approval wiring removed).
  _options?: Record<string, unknown>,
): Promise<boolean> {
  return sendNotification({
    userId,
    type: "defect_status_changed",
    entityType: "defect",
    entityId: defectId,
    payload: {
      entity_title: defectTitle,
      status: newStatus,
    },
  });
}

export async function notifyInspectionCompleted(
  userId: string,
  inspectionId: string,
  checklistName: string,
  positionName?: string,
): Promise<boolean> {
  return sendNotification({
    userId,
    type: "inspection_completed",
    entityType: "inspection",
    entityId: inspectionId,
    payload: {
      entity_title: checklistName,
      position_name: positionName,
    },
  });
}

export async function notifyDueReminder(
  userId: string,
  entityType: "inspection" | "task",
  entityTitle: string,
  entityId: string,
  dueDate: Date,
): Promise<boolean> {
  const formattedDate = dueDate.toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });

  return sendNotification({
    userId,
    type: "due_reminder",
    entityType,
    entityId,
    payload: {
      entity_title: entityTitle,
      due_date: formattedDate,
    },
  });
}

export async function notifyOverdue(
  userId: string,
  entityType: "inspection" | "task",
  entityTitle: string,
  entityId: string,
  daysOverdue?: number,
  isFirstReminder?: boolean,
): Promise<boolean> {
  return sendNotification({
    userId,
    type: "overdue",
    entityType,
    entityId,
    payload: {
      entity_title: entityTitle,
      days_overdue: daysOverdue,
      is_first_reminder: isFirstReminder,
    },
  });
}

export async function notifyDefectComment(
  assigneeId: string,
  defectTitle: string,
  defectId: string,
  commenterName?: string,
): Promise<boolean> {
  return sendNotification({
    userId: assigneeId,
    type: "defect_comment",
    entityType: "defect",
    entityId: defectId,
    payload: {
      entity_title: defectTitle,
      commenter_name: commenterName,
    },
  });
}

export async function notifyDefectClosed(
  userId: string,
  defectTitle: string,
  defectId: string,
  closedByName?: string,
): Promise<boolean> {
  return sendNotification({
    userId,
    type: "defect_closed",
    entityType: "defect",
    entityId: defectId,
    payload: {
      entity_title: defectTitle,
      closed_by_name: closedByName,
    },
  });
}

/**
 * Server-side bulk reassignment notification.
 * Sends a single edge function call with all entity IDs.
 * The server handles the fan-out internally.
 */
export async function notifyBulkReassignment(
  entities: Array<{
    entityType: "inspection" | "defect";
    entityId: string;
    entityTitle: string;
    creatorId?: string | null;
  }>,
  newUserId: string,
  newAssigneeName?: string,
): Promise<boolean> {
  try {
    const { data, error } = await supabase.functions.invoke("send-notification-email", {
      body: {
        user_id: "__bulk_reassignment__",
        notification_type: "assignment",
        payload: {
          new_user_id: newUserId,
          assignee_name: newAssigneeName,
          entities,
        },
      },
    });

    if (error) {
      // TRANSPORT channel: edge function call failed.
      logger.error("Bulk reassignment transport error", {
        entityCount: entities.length,
        ...transportSignal(error),
      });
      return false;
    }

    if (
      !data ||
      (typeof data === "object" &&
        data !== null &&
        "error" in data &&
        (data as Record<string, unknown>).error)
    ) {
      // APPLICATION channel: edge function returned an error body.
      logger.error("Bulk reassignment application error", {
        entityCount: entities.length,
        hasErrorBody: !!(data as Record<string, unknown> | null)?.error,
      });
      return false;
    }

    logger.info("Bulk reassignment sent", { entityCount: entities.length });
    return true;
  } catch (error) {
    // TRANSPORT channel: thrown before/while invoking the edge function.
    logger.error("Bulk reassignment transport exception", {
      entityCount: entities.length,
      ...transportSignal(error),
    });
    return false;
  }
}
